import axios from 'axios'
import { enrollmentService, reimbursementService } from '@hixme/api'
import {
  actions as authActions,
  logout as authLogout,
  selectors as authSelectors,
} from '@hixme/auth-ui'
import { getFamilyShape } from '@hixme/person-utils'
import { ROLE_BROKER, ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import client from 'apollo/client'
import { camelCase, upperFirst } from 'lodash'
import { browserHistory } from 'react-router'
import { callWaiter } from 'redux-waiter'

import { getEpsBaseUrl } from '../../../modules/api/eps-base-url'
import {
  buildSyntheticMembership,
  fetchMyMemberships,
  MEMBERSHIPS_FETCH_TIMEOUT_MS,
} from '../../../modules/api/employee-memberships.api'
import { exchangePerson } from '../../../modules/api/person-exchange.api'
import { fetchMe } from '../../../modules/api/me.api'
import { whenEpsSessionReady } from 'helpers/epsSession'
import {
  clearShpSessionIfBridgedEmployeeMismatch,
  clearShpSessionOnIdentityChange,
} from 'helpers/shopHealthSession'
import { remapBenefitsGetResponse } from '../../../modules/api/remap-benefits-get-response'
import { BENEFIT_TYPE_ICHRA } from '@hixme/benefit-types'

import {
  DELETE_FAMILY_GROUPS,
  GET_CLIENT_PAYROLL_PERIOD_FOR_USER,
  GET_OR_CREATE_GROUPS,
} from 'apollo/queries'
// project
import notification from 'modules/notification-manager'
import { createErrorNotification } from 'modules/notification-manager/actions'
import {
  ACCIDENT_PRODUCTS_PATH,
  ADDITIONAL_PRODUCTS_PATH,
  BASIC_LIFE_PRODUCTS_PATH,
  CHIROPRACTIC_PRODUCTS_PATH,
  CRITICAL_ILLNESS_PRODUCTS_PATH,
  DCFSA_PRODUCTS_PATH,
  DENTAL_PRODUCTS_PATH,
  EMPLOYEE_ASSISTANCE_PRODUCTS_PATH,
  FSA_PRODUCTS_PATH,
  FTA_PRODUCTS_PATH,
  GROUP_HEALTH_PATH,
  HEALTH_PRODUCTS_PATH,
  HOSPITAL_INDEMNITY_PRODUCTS_PATH,
  LEGAL_PLAN_PRODUCTS_PATH,
  LEGAL_SHIELD_PRODUCTS_PATH,
  LONG_TERM_DISABILITY_PRODUCTS_PATH,
  LPFSA_PRODUCTS_PATH,
  SHORT_TERM_DISABILITY_PRODUCTS_PATH,
  VISION_PRODUCTS_PATH,
  VOLUNTARY_AD_D_PRODUCTS_PATH,
  VOLUNTARY_LIFE_PRODUCTS_PATH,
  VOLUNTARY_LIFE_SIMPLE_PRODUCTS_PATH,
  VOLUNTARY_LONG_TERM_DISABILITY_PRODUCTS_PATH,
  VOLUNTARY_SHORT_TERM_DISABILITY_PRODUCTS_PATH,
} from 'routes/path-names'
import cart from 'store/modules/cart'
import personsModule from 'store/modules/persons'

import { getProviderFullName } from 'routes/Provider/utils/index.js'

// module
import { getEmployeeKey, getPersonsSelector } from '../persons/selectors.js'
import t from './actionTypes.js'
import {
  associatePersonToProvider,
  dissociatePersonFromProvider,
  getAllProvidersByEmployeeId,
  removeAllPersonsFromProvider,
} from './api/index.js'
import { ATTESTATIONS_POST, GET_USER_BENEFITS, PATCH_PROVIDER_SEARCH_CHECK } from './constants'
import * as selectors from './selectors'

import { evaluateEmpContributionGroup } from 'helpers/evalutateContributionGroup'
import { isPersonSelectedByProviderId } from '../user-session/selectors'
import dayjs from 'dayjs'

// Admin/broker Login-As target. Kept in sessionStorage (per tab) so concurrent
// Login-As sessions in different tabs can't overwrite each other's target (EMS-10370).
const HIXME_USER_SESSION = 'hxus-session'
const ASSUME_PATH_REGEX = /^\/admin\/assume\/([^/]+)/

export const setScrolledToBottom = () => ({
  type: t.SCROLLED_TO_BOTTOM,
})

export const setUserId = (userId) => ({
  type: t.SET_USER_ID,
  payload: userId,
})

export const loadUser = (user) => {
  return {
    type: t.LOAD_USER,
    payload: user,
  }
}

export const loadPayroll = (payroll) => ({
  type: t.LOAD_PAYROLL,
  payload: payroll,
})

export const loadClientSettings = (payload) => ({
  type: t.LOAD_CLIENT_SETTINGS,
  payload,
})

export const getEnrollmentCutoffTime = (payload) => ({
  type: t.LOAD_ENROLLMENT_CUTOFF_TIME,
  payload,
})

export const loadClientBenefitsEnrollment = (payload) => ({
  type: t.LOAD_CLIENT_BENEFITS_ENROLLMENT,
  payload,
})

export const loadEnrollmentSession = (payload) => ({
  type: t.LOAD_ENROLLMENT_SESSION,
  payload,
})

export const loadFamilyGroups = (payload) => ({
  type: t.SET_FAMILY_GROUPS,
  payload,
})

export const deleteFamilyGroups = () => ({
  type: t.DELETE_FAMILY_GROUPS,
})

export const loadEnrollmentResponses = (payload) => ({
  type: t.LOAD_ENROLLMENT_RESPONSES,
  payload,
})

export const loadPreviousEnrollment = (payload) => ({
  type: t.LOAD_PREVIOUS_ENROLLMENT,
  payload,
})

export const loadICHRAInformation = (payload) => ({
  type: t.LOAD_ICHRA_INFORMATION,
  payload,
})

export const loadMemberships = (payload) => ({
  type: t.LOAD_MEMBERSHIPS,
  payload,
})

export const loadPausedState = (payload) => ({
  type: t.LOAD_PAUSED_STATE,
  payload,
})

/**
 * Re-fetches the paused signal from /me and updates the store, to keep it fresh without a full
 * re-bootstrap (e.g. on window focus / tab visibility — see `helpers/pausedStateRefresh`).
 * Fails open (any error leaves the current state unchanged), de-dupes concurrent calls, and
 * no-ops when there is no user or a bootstrap is in progress (so it can't clobber a newer
 * `initializeUser`).
 */
let pausedRefreshInFlight = false
export const refreshPausedState = () => (dispatch, getState) => {
  const state = getState()
  const userId = selectors.getUserId(state)
  if (pausedRefreshInFlight || !userId || selectors.sessionIsUpdating(state)) {
    return Promise.resolve()
  }
  pausedRefreshInFlight = true
  return whenEpsSessionReady()
    .then(() => fetchMe())
    .then((paused) => {
      // Drop a stale result if the identity changed or a bootstrap started while this
      // refresh was in flight, so it can't clobber a newer initializeUser's paused state.
      const current = getState()
      if (selectors.getUserId(current) !== userId || selectors.sessionIsUpdating(current)) {
        return
      }
      dispatch(loadPausedState(paused))
    })
    .catch(() => {})
    .then(() => {
      pausedRefreshInFlight = false
    })
}

export const updatingUser = () => ({
  type: t.UPDATING_USER,
})

export const userSessionError = (error) => ({
  type: t.USER_INITIALIZE_ERROR,
  payload: error,
})

export const userInitialized = () => ({
  type: t.USER_INITIALIZED,
})

export const getAttestationsItem = (id) => localStorage.getItem(`attestations-route-${id}`)

export const setAttestationsItem = (id) => {
  localStorage.setItem(`attestations-route-${id}`, true)
}

export const removeAttestationsItem = () => {
  const key = Object.keys(localStorage).find((i) => i.includes('attestations-route'))
  localStorage.removeItem(key)
}

export const setUserLocation = (payload) => ({
  type: t.SET_USER_LOCATION,
  payload,
})

export const setProviderQuestionCheck = (payload) => ({
  type: t.SET_PROVIDER_QUESTION,
  payload,
})

export const loadProviders = (payload) => ({
  type: t.LOAD_PROVIDERS,
  payload,
})

export const addProvider = (payload) => ({
  type: t.ADD_PROVIDER,
  payload,
})

export const removeProvider = (payload) => ({
  type: t.REMOVE_PROVIDER,
  payload,
})

export const deleteProvider = (payload) => ({
  type: t.DELETE_ALL_FROM_PROVIDER,
  payload,
})

export const setFamilyGroups = (groupInformation) => (dispatch) => {
  dispatch(loadFamilyGroups(groupInformation))
  dispatch(cart.actions.getCartByEnrollmentPublicKey())
}

export const patchQuestionResponse = (ProviderSearchCheck) => (dispatch, getState) => {
  const enrollmentPublicKey = selectors.getEnrollmentPublicKey(getState())

  return dispatch(
    callWaiter(PATCH_PROVIDER_SEARCH_CHECK, {
      requestCreator: () =>
        enrollmentService({
          route: 'current/providerSearchCheck/{enrollmentPublicKey}',
          method: 'PATCH',
          params: {
            enrollmentPublicKey,
          },
          body: {
            ProviderSearchCheck,
          },
        }),
    })
  ).then(
    () => dispatch(setProviderQuestionCheck(ProviderSearchCheck)),
    (err) => {
      dispatch(createErrorNotification('Error registering your answer for Provider'))
      return Promise.reject(err)
    }
  )
}

export const getEnrollmentResponses = () => (dispatch, getState) => {
  const enrollmentPublicKey = selectors.getEnrollmentPublicKey(getState())
  const req = enrollmentService({
    route: '{enrollmentPublicKey}/responses',
    params: { enrollmentPublicKey },
  })

  req.then(loadEnrollmentResponses).catch((e) =>
    dispatch(
      userSessionError({
        title: 'User questions',
        message: 'We were unable to retrieve the questions required for you to enroll.',
        error: e,
      })
    )
  )

  return req
}

/** Use when you need to refresh the enrollment session
 *  Get all the enrollment data
 */
export const getEnrollmentSession = (enrollmentId) => (dispatch, getState) => {
  const enrollmentPublicKey = enrollmentId || selectors.getEnrollmentPublicKey(getState())
  if (!enrollmentPublicKey) {
    dispatch(
      notification.actions.createNotification({
        type: 'error',
        message:
          'We had trouble loading your session. No enrollment found. Please reload the page or contact us.',
      })
    )

    dispatch(
      userSessionError({
        title: 'Enrollment Session',
        message: 'We were unable to retrieve the enrollment session required for you to enroll.',
        error: 'N/A',
      })
    )

    return Promise.reject(new Error('No enrollment public key found'))
  }

  const req = enrollmentService({
    route: '{enrollmentPublicKey}',
    params: { enrollmentPublicKey },
  })

  req
    .then((res) => dispatch(loadEnrollmentSession(res)))
    .catch((e) =>
      dispatch(
        userSessionError({
          title: 'Enrollment Session',
          message: 'We were unable to retrieve the enrollment session required for you to enroll.',
          error: e,
        })
      )
    )

  return req
}

export const isValidId = (id) =>
  typeof id === 'string' && id.length >= 36 && id !== 'undefined' && id !== 'null'

/** Login-As target from an `/admin/assume/:personPublicKey/...` path, if valid. */
export const getAssumeUserIdFromPath = (pathname) => {
  const match = ASSUME_PATH_REGEX.exec(pathname || '')
  return match && isValidId(match[1]) ? match[1] : undefined
}

export const initUserId = (userId, persistUser) => (dispatch, getState) => {
  const { userRole, personPublicKey } = authSelectors.getCredentials(getState())
  const isAdminOrBroker =
    !!userRole && [ROLE_PLATFORM_HIXME_ADMIN, ROLE_BROKER].indexOf(userRole) > -1
  const storedId = sessionStorage.getItem(HIXME_USER_SESSION)
  const previousUserId = selectors.getUserId(getState())

  localStorage.removeItem(HIXME_USER_SESSION)
  if (!isValidId(storedId)) {
    sessionStorage.removeItem(HIXME_USER_SESSION)
  }

  let nextUserId
  if (isAdminOrBroker) {
    // On the Login-As route the URL is authoritative, so the layout's first bootstrap
    // loads the requested employee rather than whatever this tab stored before. Persist
    // it here too: SetUserSessionContainer may never mount (T&C gate redirects to '/',
    // bootstrap error), and a later reload must stay on this employee.
    const assumeUserId = userId ? undefined : getAssumeUserIdFromPath(window.location.pathname)
    const idToPersist = persistUser && isValidId(userId) ? userId : assumeUserId
    if (idToPersist) {
      sessionStorage.setItem(HIXME_USER_SESSION, idToPersist)
    }

    const validStoredId = isValidId(storedId) ? storedId : null
    nextUserId = userId || assumeUserId || validStoredId || personPublicKey
    dispatch(setUserId(nextUserId))
    dispatch(loadICHRAInformation({}))
  } else {
    // Employee multi-employer: the active Cognito identity (personPublicKey)
    // *is* the acting person after a token exchange (see switchActingEmployer),
    // so an explicit userId (picker) is only needed to seed the very first
    // render before credentials propagate.
    nextUserId = isValidId(userId) ? userId : personPublicKey
    dispatch(setUserId(nextUserId))
  }

  // Impersonation target change: setupEpsSessionSync re-mints the cookie; don't race it
  // with a clear here.
  if (isAdminOrBroker && nextUserId !== personPublicKey) return

  if (previousUserId && nextUserId && previousUserId !== nextUserId) {
    clearShpSessionOnIdentityChange()
  } else {
    clearShpSessionIfBridgedEmployeeMismatch(nextUserId)
  }
}

export const postAttestations = (vals) => (dispatch, getState) => {
  const enrollmentKey = selectors.getEnrollmentPublicKey(getState())
  return dispatch(
    callWaiter(ATTESTATIONS_POST, {
      requestCreator: () =>
        enrollmentService({
          route: '{enrollmentKey}/responses/OnExchange',
          method: 'POST',
          params: { enrollmentKey },
          body: vals,
        }),
    })
  ).then((res) => dispatch(loadEnrollmentSession(res)))
}

export const getBundleGroups = () => (dispatch, getState) => {
  const enrollmentKey = selectors.getEnrollmentPublicKey(getState())

  return dispatch(
    callWaiter('GET_GROUP_SPLIT', {
      requestCreator: () =>
        enrollmentService({
          route: '{enrollmentKey}/bundlegroups',
          params: { enrollmentKey },
        }),
    })
  ).then((res) => dispatch(getEnrollmentSession(enrollmentKey)).then(() => res))
}

export const putBundleGroups = (body) => (dispatch, getState) => {
  const enrollmentKey = selectors.getEnrollmentPublicKey(getState())

  return dispatch(
    callWaiter('PUT_GROUP_SPLIT', {
      requestCreator: () =>
        enrollmentService({
          route: '{enrollmentKey}/bundlegroups',
          method: 'PUT',
          params: { enrollmentKey },
          body: { ...body },
        }),
    })
  )
    .then(() => {
      dispatch(getEnrollmentSession(enrollmentKey))
    })
    .catch((err) => {
      dispatch(
        createErrorNotification(
          `There was a problem saving your Bundle groups. Please refresh and try again. ${err}`
        )
      )
    })
}

export const resetPutBundleGroups = () => (dispatch, getState) => {
  const enrollmentKey = selectors.getEnrollmentPublicKey(getState())

  return dispatch(
    callWaiter('RESET_PUT_GROUP_SPLIT', {
      requestCreator: () =>
        enrollmentService({
          route: '{enrollmentKey}/bundlegroups/reset',
          method: 'PUT',
          params: { enrollmentKey },
        }),
    })
  )
    .then(() => {
      dispatch(getEnrollmentSession(enrollmentKey))
    })
    .catch((err) => {
      dispatch(
        createErrorNotification(
          `There was a problem saving your Bundle groups. Please refresh and try again. ${err}`
        )
      )
    })
}

export const getICHRAInformation = (params) => (dispatch) =>
  dispatch(
    callWaiter('GET_ICHRA_DATA', {
      requestCreator: () =>
        reimbursementService({
          route:
            'reimbursements/clientId/{clientId}/employeeId/{userId}/benefitEffectiveDate/{benefitEffectiveDate}/expense',
          method: 'GET',
          params,
        }),
    })
  )
    .then((res) => {
      dispatch(
        loadICHRAInformation({
          available: res.expenseShow,
          amount: res.expenseAmount,
        })
      )
    })
    .catch(() => {
      dispatch(createErrorNotification(`There was a problem getting your ICHRA information`))
    })

export const getPayroll = (userId) => async (dispatch) => {
  const { data } = await client.query({
    query: GET_CLIENT_PAYROLL_PERIOD_FOR_USER,
    variables: { employeeId: userId },
  })
  dispatch(loadPayroll(data.getClientPayrollPeriodForUser))
}

// A saved provider is matched against the person_provider.providerPublicKey
// NPI at the time it was added. EPS's `npi` is whichever NPI Ideon currently
// reports as primary for that facility, which can rotate — so recognizing
// that a returned provider corresponds to *some* saved association (and
// which currentPeople rows are associated with it) needs to check the full
// `npis` array, not just the single current-primary `npi`, or a provider
// whose primary NPI rotated since it was saved would wrongly appear to have
// no saved association at all. Read-only: add/remove use the exact saved
// NPI carried per-person on provider.selected (see getPersonsByProvider)
// instead of guessing across npis[], since person_provider already records
// precisely which NPI each association was saved under.
// Accepts either the raw EPS provider shape (`npi`) or the displayed/stored
// provider shape (`id`, after getProviders() renames npi -> id) as the
// single-value fallback — both carry `npis` under the same key.
const getProviderNpis = ({ npi, id, npis }) => (npis && npis.length ? npis : [npi ?? id])

export const getProviders = () => async (dispatch, getState) => {
  try {
    const { Person, Dependents } = getPersonsSelector(getState())
    const family = [Person, ...Dependents].reduce(
      (prev, curr) => ({ ...prev, [curr.Id]: curr }),
      {}
    )
    const getPersonsByProvider = (data, npis) =>
      data
        .filter((item) => npis.includes(item.providerPublicKey))
        .map((person) => {
          const member = family[person.personPublicKey]
          return {
            id: member.Id,
            name: member.FullName,
            // The exact NPI this specific association was saved under (may
            // differ from the provider's current-primary npi/id after an
            // Ideon rotation) — add/remove use this directly instead of
            // guessing across every NPI variant Ideon reports.
            providerPublicKey: person.providerPublicKey,
          }
        })
    const addressFormatter = (address) => ({
      id: address.id,
      streetAddress: `${address.street_line_1}`,
      city: address.city,
      stateProvince: address.state,
      postalCode: address.zip_code,
      phoneNumber: address.phone_numbers.pop(),
    })

    const EmployeePublicKey = getEmployeeKey(getState())
    const { persons, providers } = await dispatch(getAllProvidersByEmployeeId(EmployeePublicKey))
    const currentPeople = persons.filter((person) => family[person.personPublicKey])
    const familyProviders = currentPeople.map((person) => person.providerPublicKey)
    const filteredProviders = providers.filter((provider) =>
      getProviderNpis(provider).some((npi) => familyProviders.includes(npi))
    )
    const myProviders = filteredProviders.map(({ addresses, name, npi, ...provider }) => {
      const selected = getPersonsByProvider(currentPeople, getProviderNpis({ npi, npis: provider.npis }))
      return {
        fullName: getProviderFullName(name),
        addresses: addresses.map((address) => addressFormatter(address)),
        ...provider,
        id: npi,
        selected,
      }
    })
    dispatch(loadProviders(myProviders))
  } catch (err) {
    dispatch(createErrorNotification('There was an error getting your Providers information'))
  }
}

export const addPersonToProvider = (params, person) => async (dispatch, getState) => {
  const EmployeePublicKey = selectors.getUserId(getState())
  try {
    dispatch(addProvider({ ...params, person }))

    const { Provider, ...body } = { ...params }

    await dispatch(associatePersonToProvider({ ...body, EmployeePublicKey }))
  } catch (err) {
    dispatch(removeProvider(params))
  }
}

export const removeProviderFromPerson = (params, person) => async (dispatch) => {
  const { Provider, ...body } = { ...params }
  // This person can carry more than one person_provider row for the same
  // facility — e.g. saved under OLD_NPI, then Ideon rotated the facility's
  // primary NPI and the person was re-added under NEW_NPI without the
  // stale OLD_NPI row ever being cleaned up. getPersonsByProvider surfaces
  // every such row, so this person's id can appear more than once in
  // Provider.selected, each entry carrying a different saved NPI. Dissociate
  // every one of them (a `.find()` picking only the first left the other
  // NPI's row in person_provider — unchecking the provider appeared to
  // remove it in the optimistic UI, but a refresh brought it back).
  const savedNpis = [
    ...new Set(
      (Provider?.selected || [])
        .filter((selectedPerson) => selectedPerson.id === body.PersonPublicKey)
        .map((selectedPerson) => selectedPerson.providerPublicKey)
        .filter(Boolean)
    ),
  ]
  const npisToRemove = savedNpis.length ? savedNpis : [body.ProviderPublicKey]

  try {
    dispatch(removeProvider(params))

    // allSettled, not all: one NPI's dissociate failing must not roll back
    // a different NPI's association that was actually removed — restore
    // only the NPIs whose call failed, each under its own saved value (see
    // the rotated-NPI rollback above), so a retry targets the right row
    // instead of the current-primary one.
    const results = await Promise.allSettled(
      npisToRemove.map((providerPublicKey) =>
        dispatch(dissociatePersonFromProvider({ ...body, ProviderPublicKey: providerPublicKey }))
      )
    )

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        dispatch(addProvider({ ...params, providerPublicKey: npisToRemove[index], person }))
      }
    })
  } catch (err) {
    npisToRemove.forEach((providerPublicKey) =>
      dispatch(addProvider({ ...params, providerPublicKey, person }))
    )
  }
}

/** Resolves once add/remove provider waiters finish — used before leaving provider lookup. */
export const waitForProviderMutations = () => (_, getState) => {
  const isPending = () => selectors.isCreatingOrUpdating(getState())

  if (!isPending()) return Promise.resolve()

  return new Promise((resolve) => {
    const poll = () => {
      if (!isPending()) {
        resolve()
        return
      }
      setTimeout(poll, 50)
    }
    poll()
  })
}

export const removeAllFromProvider = (provider) => (dispatch, getState) => {
  const ProviderPublicKey = provider.id
  try {
    const { Person } = getPersonsSelector(getState())
    // Delete against the exact NPI(s) these associations were actually saved
    // under (carried per-person on provider.selected — see
    // getPersonsByProvider), not every NPI variant Ideon reports for the
    // facility: some of those may never have been saved to person_provider
    // at all. Falls back to provider.id if selected somehow carries none
    // (e.g. a provider entry with no associations).
    const savedNpis = [
      ...new Set((provider.selected || []).map((p) => p.providerPublicKey).filter(Boolean)),
    ]
    const npisToDelete = savedNpis.length ? savedNpis : [ProviderPublicKey]
    npisToDelete.forEach((npi) =>
      dispatch(
        removeAllPersonsFromProvider({
          ProviderPublicKey: npi,
          EmployeePublicKey: Person.Id,
        })
      )
    )
    dispatch(deleteProvider({ ProviderPublicKey }))
  } catch (err) {
    dispatch(createErrorNotification('Something went wrong removing the provider.'))
  }
}

export const togglePersonProvider = (params) => (dispatch, getState) => {
  const { Person, Dependents } = getPersonsSelector(getState())
  const person = [Person, ...Dependents].find((member) => member.Id === params.PersonPublicKey)

  const isAdded = isPersonSelectedByProviderId(
    params.ProviderPublicKey,
    params.PersonPublicKey,
    getState()
  )
  isAdded
    ? dispatch(removeProviderFromPerson(params, person))
    : dispatch(addPersonToProvider(params, person))
}

export const getBenefits = ({ EmployeePublicKey, EffectiveOn = '' }) => (dispatch) =>
  dispatch(
    callWaiter(`${GET_USER_BENEFITS}_${EffectiveOn}`, {
      requestCreator: () =>
        axios
          .get(`${getEpsBaseUrl()}/core/benefits/${EmployeePublicKey}`, { params: { EffectiveOn } })
          .then(({ data }) => remapBenefitsGetResponse(data)),
    })
  )

const buildSyntheticMembershipFromState = (state, userId) => {
  const clientPublicKey = selectors.getClientPublicKey(state) || selectors.getClientId(state) || ''
  const companyName = selectors.getCompanyName(state) || ''
  return buildSyntheticMembership({
    personId: userId,
    clientPublicKey,
    companyName,
  })
}

/** Bumped on each initializeUser so late memberships fetches cannot overwrite a newer bootstrap. */
let initializeUserGeneration = 0

export const initializeUser = () => (dispatch, getState) => {
  const userId = selectors.getUserId(getState())

  if (!userId) {
    dispatch(logout())
    return null
  }

  const generation = ++initializeUserGeneration
  const isStaleBootstrap = () => generation !== initializeUserGeneration

  dispatch(updatingUser())
  // Drop prior employer's enrollment/cart before refetch so a failed bootstrap
  // cannot leave old company context under a new Cognito identity (employer switch).
  dispatch(loadEnrollmentSession({}))
  dispatch(loadEnrollmentResponses([]))
  dispatch(loadClientSettings({}))
  dispatch(loadClientBenefitsEnrollment({}))
  dispatch(loadPreviousEnrollment(undefined))
  dispatch(loadICHRAInformation({}))
  dispatch(cart.actions.resetCart())
  // Reset the paused signal too. Otherwise a prior identity's pause survives into this
  // bootstrap (employer switch / impersonation): if /me then fails or times out below,
  // loadPausedState is skipped and the stale flag leaks onto the new identity — a
  // fail-CLOSED lockout. Defaulting to not-paused here keeps the contract fail-open until
  // /me re-confirms.
  dispatch(loadPausedState(null))

  dispatch(getPayroll(userId))

  // Get Person
  const getPerson = dispatch(personsModule.actions.getFamilyByEmployeeId(userId))
    .then((response) => {
      const family = getFamilyShape(response.Persons)
      dispatch(loadUser(family.employee))
      return response
    })
    .catch((e) =>
      dispatch(
        userSessionError({
          title: 'Family information',
          message: 'We were unable to retrieve your user/family data.',
          error: e,
        })
      )
    )

  const getICHRAEffectiveDate = dispatch(
    getBenefits({
      EmployeePublicKey: userId,
      EffectiveOn: '',
    })
  )
    .then(({ Benefits }) => {
      const currentICHRABenefits =
        Benefits.filter((benefit) => benefit.BenefitType === BENEFIT_TYPE_ICHRA) || null
      if (!currentICHRABenefits) return null
      if (currentICHRABenefits.length > 1) {
        const [currentBenefit] = currentICHRABenefits.filter(
          (current) => !current.BenefitEndDateOld
        )
        return currentBenefit.BenefitEffectiveDate
      }
      return currentICHRABenefits[0].BenefitEffectiveDate
    })
    .catch(() => null)

  const session = enrollmentService({
    route: 'current/{userId}',
    params: { userId },
  })
    .then((enrollment) => {
      const {
        Cart,
        Responses,
        Enrollment,
        Client,
        BenefitCounts,
        ClientBenefitsEnrollment,
        PreviousEnrollment,
      } = enrollment
      // Missing required pieces: record errors and do not load partial/stale data
      // (employer-scoped state was already cleared at initializeUser start).
      if (!Cart || !Responses || !Enrollment || !Client || !BenefitCounts) {
        if (!BenefitCounts) {
          dispatch(
            userSessionError({
              title: 'Benefits',
              message: 'We were unable to retrieve your company benefits.',
              error: new Error('n/a'),
            })
          )
        }

        if (!Client) {
          dispatch(
            userSessionError({
              title: 'Client',
              message: 'We were unable to retrieve your company.',
              error: new Error('n/a'),
            })
          )
        }

        if (!ClientBenefitsEnrollment) {
          dispatch(
            userSessionError({
              title: 'Client Benefits Enrollment',
              message: 'We were unable to retrieve your company benefits enrollment information.',
              error: new Error('n/a'),
            })
          )
        }

        if (!Enrollment) {
          dispatch(
            userSessionError({
              title: 'Enrollment session',
              message: 'We were unable to retrieve your enrollment session.',
              error: new Error('n/a'),
            })
          )
        }

        if (!Cart) {
          dispatch(
            userSessionError({
              title: 'Enrollment Cart',
              message: 'We were unable to retrieve your enrollment cart.',
              error: new Error('n/a'),
            })
          )
        }

        if (!Responses) {
          dispatch(
            userSessionError({
              title: 'Enrollment Responses',
              message: 'We were unable to retrieve your enrollment session responses.',
              error: new Error('n/a'),
            })
          )
        }
        return undefined
      }

      dispatch(cart.actions.loadCart(Cart))
      // Client settings requires BenefitCounts to be included
      dispatch(loadClientSettings({ ...Client, BenefitCounts }))
      dispatch(loadClientBenefitsEnrollment(ClientBenefitsEnrollment))
      dispatch(loadEnrollmentResponses(Responses))
      dispatch(loadEnrollmentSession(Enrollment))
      if (PreviousEnrollment) {
        dispatch(loadPreviousEnrollment(PreviousEnrollment))
      } else {
        // This is to clear any old data
        dispatch(loadPreviousEnrollment(undefined))
      }

      return enrollment
    })
    .catch((e) => {
      dispatch(
        notification.actions.createNotification({
          type: 'error',
          message: 'We had trouble loading your session. Please reload the page',
        })
      )

      dispatch(
        userSessionError({
          title: 'User Session',
          message: 'We were unable to retrieve your session.',
          error: e,
        })
      )
      return undefined
    })

  // Memberships bootstrap: gate AppLoader.
  // Success with data → replace. Empty success → clear (server authority).
  // Timeout/error → keep existing memberships. Synthetic only for cold start
  // when store has none (isPrimary: false — never claim home). Late timeout
  // success applies only if this bootstrap generation is still current.
  const membershipsRequest = fetchMyMemberships()
    .then((data) => ({ status: 'ok', data: Array.isArray(data) ? data : [] }))
    .catch(() => ({ status: 'error' }))

  const membershipsOutcome = Promise.race([
    membershipsRequest,
    new Promise((resolve) => {
      setTimeout(() => resolve({ status: 'timeout' }), MEMBERSHIPS_FETCH_TIMEOUT_MS)
    }),
  ])

  const applyMembershipsFallback = () => {
    if (isStaleBootstrap()) return
    const existing = selectors.getMemberships(getState())
    if (Array.isArray(existing) && existing.length > 0) {
      return
    }
    const synthetic = buildSyntheticMembershipFromState(getState(), userId)
    dispatch(loadMemberships(synthetic ? [synthetic] : []))
  }

  // Paused signal from the EPS BFF /me payload, normalized to { isPaused, isPausedForActor }.
  // Fail open (null → treated as not paused) on error, and bound the wait so a hung /me can't
  // stall the app loader. /me is cookie-guarded, so it requires the access_token cookie
  // established by the EPS bridge session to authenticate.
  let pausedTimeoutId
  const pausedOutcome = Promise.race([
    // Wait for the bridge-login cookie to be established before calling cookie-guarded /me,
    // so the first fetch can't race the mint and 401 (which would fail open to not-paused).
    whenEpsSessionReady()
      .then(() => fetchMe())
      .catch(() => null),
    new Promise((resolve) => {
      pausedTimeoutId = setTimeout(() => resolve(null), MEMBERSHIPS_FETCH_TIMEOUT_MS)
    }),
  ])

  return Promise.all([
    getPerson,
    session,
    getICHRAEffectiveDate,
    membershipsOutcome,
    pausedOutcome,
  ]).then(async (res) => {
    clearTimeout(pausedTimeoutId)
    if (isStaleBootstrap()) return
    try {
      const [, userSession, ichraDate, membershipsResult, pausedResult] = res
      // Persist memberships before ICHRA so an ICHRA failure cannot wipe the picker.
      if (membershipsResult.status === 'ok') {
        dispatch(loadMemberships(membershipsResult.data))
      } else {
        applyMembershipsFallback()
        if (membershipsResult.status === 'timeout') {
          membershipsRequest
            .then((late) => {
              if (isStaleBootstrap()) return
              if (late.status === 'ok' && Array.isArray(late.data)) {
                dispatch(loadMemberships(late.data))
              }
            })
            .catch(() => {})
        }
      }

      // Paused state: apply when /me resolved in time; on error/timeout leave defaults (not paused).
      if (pausedResult) {
        dispatch(loadPausedState(pausedResult))
      }

      if (ichraDate) {
        try {
          await dispatch(
            getICHRAInformation({
              clientId: userSession.Enrollment.ClientPublicKey,
              userId: userSession.Enrollment.EmployeePublicKey,
              benefitEffectiveDate: ichraDate,
            })
          )
        } catch (ichraErr) {
          // eslint-disable-next-line no-console
          console.debug(ichraErr)
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug(err)
      applyMembershipsFallback()
    }
    if (isStaleBootstrap()) return
    // only initialize the user after family, cart, enrollment, and memberships settle
    dispatch(userInitialized())
  })
}

/** In-flight employer switch — concurrent picks are ignored until this settles. */
let actingEmployerSwitchPromise = null

/** True when bootstrap left the session unusable (error view / empty enrollment). */
export const didUserBootstrapFail = (state) => {
  const errors = selectors.sessionErrorsSelector(state) || []
  if (errors.length > 0) return true
  const enrollment = selectors.getEnrollmentSession(state) || {}
  return !enrollment.Id
}

/**
 * Switch this tab's acting employer Person by exchanging the current
 * Cognito identity for the target Person's (see EPS `POST
 * /api/v1/auth/person-exchange`), then re-bootstrap enrollment/family data
 * and land on `redirectTo` (default home) so company-specific screens are
 * not left mid-flow. Pass `redirectTo: null` to stay on the current route
 * (e.g. Settings → Login Preferences).
 *
 * Skips the exchange when already holding tokens for `personId` (covers
 * both the "already on target" and "already on primary, selecting primary"
 * cases); otherwise always exchanges, including switching back to primary
 * from a secondary identity.
 *
 * Shows AppLoader via `updatingUser` for the whole exchange+bootstrap window
 * so the header picker cannot start a second overlapping switch.
 *
 * If exchange succeeds but bootstrap fails, restores the previous Cognito
 * credentials and re-bootstraps so the user is not stuck on UserSessionErrorView
 * without an employer picker.
 */
export const switchActingEmployer = (personId, { redirectTo = '/' } = {}) => async (
  dispatch,
  getState
) => {
  if (isValidId(personId) !== true) {
    return Promise.resolve()
  }
  // Admin/broker Login-As keeps Cognito on the admin identity; do not person-exchange.
  if (selectors.isImpersonating(getState())) {
    return Promise.resolve()
  }
  if (actingEmployerSwitchPromise) {
    return Promise.resolve()
  }

  const navigateAfterSwitch = () => {
    if (typeof redirectTo === 'string' && redirectTo.length > 0) {
      browserHistory.push(redirectTo)
    }
  }

  actingEmployerSwitchPromise = (async () => {
    // Block UI before exchangePerson so credentials cannot race mid-flight.
    dispatch(updatingUser())

    const previousCredentials = authSelectors.getCredentials(getState()) || {}
    const previousPersonId = previousCredentials.username
    let didExchange = false

    if (previousCredentials.username !== personId) {
      try {
        const newCredentials = await exchangePerson(personId)
        dispatch(authActions.loginUserSuccess(newCredentials))
        didExchange = true
      } catch (err) {
        // Clear updatingUser; enrollment/cart untouched (initializeUser not reached).
        // Caller (HeaderContainer) surfaces a user-friendly error notification.
        dispatch(userInitialized())
        throw err
      }
    }

    dispatch(initUserId(personId, false))
    await Promise.resolve(dispatch(initializeUser()))

    if (didExchange && didUserBootstrapFail(getState())) {
      dispatch(
        createErrorNotification('Unable to load that company. Restored your previous session.')
      )
      dispatch(authActions.loginUserSuccess(previousCredentials))
      if (isValidId(previousPersonId) === true) {
        dispatch(initUserId(previousPersonId, false))
        await Promise.resolve(dispatch(initializeUser()))
      } else {
        dispatch(userInitialized())
      }
      if (didUserBootstrapFail(getState())) {
        throw new Error('Employer switch bootstrap failed')
      }
      // Same as success path — leave any company-specific route for the failed target.
      navigateAfterSwitch()
      return
    }

    navigateAfterSwitch()
  })().finally(() => {
    actingEmployerSwitchPromise = null
  })

  return actingEmployerSwitchPromise
}

export const resetUser = () => (dispatch) => {
  clearShpSessionOnIdentityChange()
  sessionStorage.removeItem(HIXME_USER_SESSION)
  localStorage.removeItem('bundle-groups')
  removeAttestationsItem()
  // Leave /admin/assume/... first, or initUserId would re-adopt (and re-persist) its target.
  browserHistory.replace('/')
  dispatch(initUserId())
  dispatch(initializeUser())
  dispatch(
    notification.actions.createNotification({
      message: 'Logging out of user...',
    })
  )

  setTimeout(() => {
    window.location = '/'
  }, 1000)
}

export const logout = () => (dispatch) => {
  clearShpSessionOnIdentityChange()
  localStorage.removeItem('bundle-groups')
  removeAttestationsItem()
  dispatch(authLogout())
  setTimeout(() => {
    window.location = '/login'
  }, 10)
}

// note about infinite loop
export const nextRouteMap = [
  HEALTH_PRODUCTS_PATH,
  GROUP_HEALTH_PATH,
  DENTAL_PRODUCTS_PATH,
  VISION_PRODUCTS_PATH,
  FSA_PRODUCTS_PATH,
  LPFSA_PRODUCTS_PATH,
  DCFSA_PRODUCTS_PATH,
  FTA_PRODUCTS_PATH,
  BASIC_LIFE_PRODUCTS_PATH,
  VOLUNTARY_LIFE_PRODUCTS_PATH,
  VOLUNTARY_LIFE_SIMPLE_PRODUCTS_PATH,
  VOLUNTARY_AD_D_PRODUCTS_PATH,
  SHORT_TERM_DISABILITY_PRODUCTS_PATH,
  LONG_TERM_DISABILITY_PRODUCTS_PATH,
  VOLUNTARY_LONG_TERM_DISABILITY_PRODUCTS_PATH,
  VOLUNTARY_SHORT_TERM_DISABILITY_PRODUCTS_PATH,
  EMPLOYEE_ASSISTANCE_PRODUCTS_PATH,
  CHIROPRACTIC_PRODUCTS_PATH,
  LEGAL_PLAN_PRODUCTS_PATH,
  LEGAL_SHIELD_PRODUCTS_PATH,
  HOSPITAL_INDEMNITY_PRODUCTS_PATH,
  CRITICAL_ILLNESS_PRODUCTS_PATH,
  ACCIDENT_PRODUCTS_PATH,
  ADDITIONAL_PRODUCTS_PATH,
]

export function getNextBenefitPath(fromPath, reverse = false, skipShop = false) {
  // There are two health bundle paths - recommended and all
  // this check normalizes the fromPath to be the base HEALTH_PRODUCTS_PATH
  // eslint-disable-next-line
  fromPath = fromPath.indexOf(HEALTH_PRODUCTS_PATH) !== -1 ? HEALTH_PRODUCTS_PATH : fromPath

  // need to clone the array for reverse, since it mutates the array
  const routeMap = reverse ? [...nextRouteMap].reverse() : nextRouteMap
  return (dispatch, getState) => {
    const state = getState()
    let nextPath
    if (!fromPath) {
      nextPath = routeMap[0]
    } else {
      nextPath = routeMap[routeMap.indexOf(fromPath) + 1]
    }

    if (!nextPath) return null

    if (HEALTH_PRODUCTS_PATH === nextPath && !skipShop) {
      if (selectors.healthBundleBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (GROUP_HEALTH_PATH === nextPath) {
      if (selectors.groupHealthBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (DENTAL_PRODUCTS_PATH === nextPath) {
      if (selectors.dentalBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VISION_PRODUCTS_PATH === nextPath) {
      if (selectors.visionBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (FSA_PRODUCTS_PATH === nextPath) {
      if (selectors.FSABenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (DCFSA_PRODUCTS_PATH === nextPath) {
      if (selectors.DCFSABenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (LPFSA_PRODUCTS_PATH === nextPath) {
      if (selectors.LPFSABenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (FTA_PRODUCTS_PATH === nextPath) {
      if (selectors.FTABenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (BASIC_LIFE_PRODUCTS_PATH === nextPath) {
      if (selectors.basicLifeBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VOLUNTARY_LIFE_PRODUCTS_PATH === nextPath) {
      if (selectors.voluntaryLifeBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VOLUNTARY_LIFE_SIMPLE_PRODUCTS_PATH === nextPath) {
      if (selectors.voluntaryLifeSimpleBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VOLUNTARY_AD_D_PRODUCTS_PATH === nextPath) {
      if (selectors.voluntaryADDBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (SHORT_TERM_DISABILITY_PRODUCTS_PATH === nextPath) {
      if (selectors.shortTermDisabilityBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (LONG_TERM_DISABILITY_PRODUCTS_PATH === nextPath) {
      if (selectors.longTermDisabilityBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VOLUNTARY_LONG_TERM_DISABILITY_PRODUCTS_PATH === nextPath) {
      if (selectors.voluntaryLongTermDisabilityBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (VOLUNTARY_SHORT_TERM_DISABILITY_PRODUCTS_PATH === nextPath) {
      if (selectors.voluntaryShortTermDisabilityBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (EMPLOYEE_ASSISTANCE_PRODUCTS_PATH === nextPath) {
      if (selectors.employeeAssistanceBenefitIsAvailable(state)) {
        return nextPath
      }
    } else if (CHIROPRACTIC_PRODUCTS_PATH === nextPath) {
      if (selectors.chiropracticBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (LEGAL_PLAN_PRODUCTS_PATH === nextPath) {
      if (selectors.legalPlanBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (LEGAL_SHIELD_PRODUCTS_PATH === nextPath) {
      if (selectors.legalShieldBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (HOSPITAL_INDEMNITY_PRODUCTS_PATH === nextPath) {
      if (selectors.hospitalIndemnityBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (ADDITIONAL_PRODUCTS_PATH === nextPath) {
      if (selectors.interestedBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (CRITICAL_ILLNESS_PRODUCTS_PATH === nextPath) {
      if (selectors.criticalIllnessBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    } else if (ACCIDENT_PRODUCTS_PATH === nextPath) {
      if (selectors.accidentBenefitIsAvailable(state)) {
        return nextPath
      }
      // fall through
    }

    return dispatch(getNextBenefitPath(nextPath, reverse))
  }
}

export const replaceWithNextBenefitRoute = (path) => (dispatch) => {
  const currentPath = typeof path === 'string' ? path : window.location.pathname
  const nextRoutePath = dispatch(getNextBenefitPath(currentPath)) || '/'
  browserHistory.replace(nextRoutePath)
}

export const pushNextBenefitRoute = (path, skipShop) => (dispatch) => {
  const currentPath = typeof path === 'string' ? path : window.location.pathname
  const nextRoutePath = dispatch(getNextBenefitPath(currentPath, false, skipShop)) || '/'
  browserHistory.push(nextRoutePath)
}

export const getNextBenefitRoute = (path, skipShop) => (dispatch) => {
  const currentPath = typeof path === 'string' ? path : window.location.pathname
  return dispatch(getNextBenefitPath(currentPath, false, skipShop)) || '/'
}

export const pushPreviousBenefitRoute = (path) => (dispatch) => {
  const currentPath = typeof path === 'string' ? path : window.location.pathname
  const nextRoutePath = dispatch(getNextBenefitPath(currentPath, true)) || '/'
  browserHistory.push(nextRoutePath)
}

export const postLoginAs = () => async (dispatch, getState) => {
  const state = getState()
  const employeeKey = selectors.getUserId(state)

  try {
    await enrollmentService({
      route: 'login-as-user/{employeeKey}',
      method: 'POST',
      params: { employeeKey },
    })
  } catch (error) {
    console.error('Enrollment Service loginAsUser had an error', error) // eslint-disable-line no-console
  }
}

const normalizeKey = (key) => upperFirst(camelCase(key))
const normalizeKeys = (payload) =>
  Object.entries(payload).reduce((prev, [key, value]) => {
    if (key === '__typename') return prev
    const normalizedKey = normalizeKey(key)
    const isArray = Array.isArray(value)
    if (isArray) {
      // eslint-disable-next-line no-use-before-define
      const result = normalizeArray(value)
      return { ...prev, [normalizedKey]: result }
    }
    return { ...prev, [normalizedKey]: value }
  }, {})

const normalizeArray = (arr) => arr.map((instance) => normalizeKeys(instance))

export const getFamilyGroups = (enrollmentPublicKey) => async (dispatch, getState) => {
  const enrollmentKey = enrollmentPublicKey || selectors.getEnrollmentPublicKey(getState())
  const {
    data: { getOrCreateFamilyGroups },
  } = await client.mutate({
    mutation: GET_OR_CREATE_GROUPS,
    variables: {
      input: {
        enrollmentPublicKey: enrollmentKey,
      },
    },
  })
  await evaluateEmpContributionGroup({
    employeePublicKey: getEmployeeKey(getState()),
    enrollmentPublicKey: enrollmentKey,
  })
  dispatch(loadFamilyGroups(normalizeKeys(getOrCreateFamilyGroups)))
  dispatch(cart.actions.getCartByEnrollmentPublicKey(enrollmentKey))
}

export const deleteFamilyMembers = () => async (dispatch, getState) => {
  const enrollmentKey = selectors.getEnrollmentPublicKey(getState())
  const familyGroups = selectors.getGroups(getState())
  if (!familyGroups) return
  await client.mutate({
    mutation: DELETE_FAMILY_GROUPS,
    variables: {
      input: {
        enrollmentPublicKey: enrollmentKey,
      },
    },
  })
  dispatch(deleteFamilyGroups())
}

export const setQleGroupNo = (qleGroupNo) => ({
  type: t.SET_QLE_GROUP_NO,
  payload: qleGroupNo,
})

export const setQleGroup = (group) => ({
  type: t.SET_QLE_GROUP,
  payload: group,
})

export const initQleAction = (qleGroupNo, currentUserId, onNotExpired) => async (dispatch) => {
  dispatch(setQleGroupNo(qleGroupNo))
  try {
    const baseUrl = getEpsBaseUrl()
    const url = `${baseUrl}/api/v1/qle-groups/no/${qleGroupNo}`

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    dispatch(setQleGroup(response.data))

    const expiryDate = response.data?.group?.expiryDate
    const isExpired = !expiryDate || dayjs(new Date()).isAfter(dayjs(new Date(expiryDate)))
    const isQLEOwner = response.data?.group?.employeePublicKey === currentUserId

    /**
     * If the QLE group is not expired and the current user is the QLE owner, then redirect to the QLE page
     * If the QLE group is expired or the current user is not the QLE owner, then redirect to the home page
     */
    if (!isExpired && isQLEOwner) {
      onNotExpired(qleGroupNo)
    } else {
      browserHistory.push('/')
    }
  } catch (err) {
    dispatch(createErrorNotification('Unable to load QLE group data.'))
    browserHistory.push('/')
  }
}
